import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { LayoutContext, PLATFORM_LOGO_SRC } from '../../../../layout-context.model';
import { AppRoutes } from '@app/core/routing';
import { IconComponent } from '@app/ui';
import { HeaderCurrencySelectComponent } from '../../shared/header-currency-select/header-currency-select.component';

/**
 * Compact chrome for focused flows (auth, giving journeys): brand mark plus a
 * single right-hand affordance, no nav / search / account menu.
 *
 * The affordance is chosen by `ctx.route.headerAction` (route data), so one bar
 * serves every focused flow instead of forking a near-duplicate header.
 *
 * On `/checkout` the bar follows the Etsy pattern: a quiet lock + "Secure
 * checkout" label sits by the logo, and the Donate CTA is suppressed (a donate
 * button mid-donation is redundant). Other minimal-header routes (auth) keep
 * their `headerAction` affordance untouched.
 */
@Component({
  selector: 'gb-header-minimal',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, HeaderCurrencySelectComponent],
  templateUrl: './header-minimal.component.html',
})
export class HeaderMinimalComponent {
  protected readonly AppRoutes = AppRoutes;

  private readonly router = inject(Router);

  @Input({ required: true }) ctx!: LayoutContext;

  /**
   * Route-data can't distinguish checkout from the other focused flows without
   * forking the slot, so the bar reads the router URL directly. `Router.url`
   * is populated on the server too, so SSR and the hydrating client agree.
   */
  protected get isCheckout(): boolean {
    return /^\/checkout(\/|\?|#|$)/.test(this.router.url);
  }

  /**
   * This bar is light (base-100), so both hosts use their colour lockup — the
   * same treatment as `header-modern`. A white-only charity mark would vanish
   * on white, so we never fall back to `logo.white` here; empty means "no
   * usable image" and the template renders a text wordmark instead, so a
   * charity without a colour logo never borrows Givebrite's.
   */
  protected get logoSrc(): string {
    if (this.ctx.app !== 'charity') {
      return PLATFORM_LOGO_SRC;
    }
    return this.ctx.charity?.logo?.color || '';
  }

  protected get brandName(): string {
    return (this.ctx.app === 'charity' ? this.ctx.charity?.name : '') || 'Givebrite';
  }
}
